import axios from 'axios'
import { getTokens, saveTokens, clearAllAuth } from './auth'
import {
  isAndroid,
  getAppType,
  androidLogout,
  androidCloseWebForm,
  pushJwtToAndroid
} from '../android/bridge'

/* ---------- helper: ?arr=a&arr=b ---------- */
const repeatArrayParams = (params = {}) => {
  const q = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) =>
    Array.isArray(v) ? v.forEach((x) => q.append(k, x)) : v != null && q.append(k, v)
  )
  return q.toString()
}

const instance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL_ADS,
  withCredentials: false,
  paramsSerializer: { serialize: repeatArrayParams }
})

/* ------------------------------------------------------------------ */
/* 1. Request interceptor – attach access token (except auth routes)  */
/* ------------------------------------------------------------------ */
instance.interceptors.request.use((cfg) => {
  const { access } = getTokens()
  const isAuthRoute = cfg.url?.includes('/login/') || cfg.url?.includes('/token/refresh/')
  if (access && !isAuthRoute) cfg.headers['JWT-Authorization'] = `Bearer ${access}`
  return cfg
})

/* ------------------------------------------------------------------ */
/* 2. Response interceptor – single refresh then *one* retry          */
/* ------------------------------------------------------------------ */
let refreshingPromise = null // shared across requests

/* ----------------------------------------------------------
   Decide what “logout” means for each runtime environment
   ---------------------------------------------------------- */
const redirectToLogin = () => {
  /* --- Android WebViews --------------------------------- */
  if (isAndroid()) {
    const app = getAppType() // 'jobs_wbv' | 'news_wbv' | 'news'
    if (app === 'jobs_wbv') {
      androidLogout()
      return
    }
    if (app.startsWith('news')) {
      androidCloseWebForm()
      return
    }
    /* fall through to browser redirect in weird edge-cases */
  }

  /* --- Classic browser ---------------------------------- */
  if (typeof window !== 'undefined') {
    if (!window.location.pathname.startsWith('/hire/signin')) {
      window.location.replace('/hire/signin')
    }
  }
}

instance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error || {}
    if (response?.status !== 401) throw error // not auth-error → bubble up

    /* ------------- Already retried once → give up ------------- */
    if (config?._retry) {
      clearAllAuth()
      redirectToLogin()
      throw error
    }

    /* ------------- No refresh token at all → logout ------------- */
    const { refresh } = getTokens()
    if (!refresh) {
      clearAllAuth()
      redirectToLogin()
      throw error
    }

    /* ------------- Refresh exactly once (shared promise) -------- */
    if (!refreshingPromise) {
      const url = `${process.env.NEXT_PUBLIC_BASE_URL_ADS}/user/token/refresh/`
      refreshingPromise = axios.post(url, { refresh }).finally(() => {
        refreshingPromise = null // reset after settle
      })
    }

    try {
      const { data } = await refreshingPromise
      saveTokens({ access: data?.access, refresh: data?.refresh ?? refresh })

      /* Updating JWT in Android shared config */
      pushJwtToAndroid({
        access: data?.access,
        refresh: data?.refresh ?? refresh
      })

      /* Mark & retry original request */
      config._retry = true // <-- guard flag
      config.headers['JWT-Authorization'] = `Bearer ${data?.access}`
      return instance.request(config)
    } catch (e) {
      /* Refresh failed – nuke auth */
      clearAllAuth()
      redirectToLogin()
      throw e // still reject
    }
  }
)

export default instance
